import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function GnulChapter2() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <div className="calculus-hero-overlay" style={{ background: 'linear-gradient(to bottom, rgba(8,8,8,0.5) 0%, rgba(8,8,8,0.8) 60%, rgba(8,8,8,1) 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: 'url(/assets/gnul-hero.png)', backgroundSize: 'cover', backgroundPosition: 'center 20%', zIndex: 1 }} />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">The Chemistry of Leadership &middot; Chapter 2</span>
          <h1>The Terminal</h1>
          <p className="calculus-hero-sub">She taught him the math. She never taught him what it would feel like to be the variable.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* The Door Opens */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              The car stops. The butler opens the door.
            </p>
            <p>
              Sound hits first. Not words. Not announcements. The hum. Hundreds of voices
              layered into a frequency he has never heard before. Low rumble of rolling luggage.
              The sharp click of heels on tile. A child laughing somewhere to the left. A phone
              ringing. Another phone ringing. The PA system cutting through with a gate change
              that nobody seems to hear.
            </p>
            <p>
              He steps out of the car and the air changes. It smells different from the cabin.
              Fuel. Coffee. Carpet cleaner. Perfume from someone who passed this spot thirty
              seconds ago. He catalogues it without meaning to. She trained him to do that. To
              read a room by the chemistry in the air before reading the faces in it.
            </p>
            <p>
              The butler is beside him. "Terminal B, Young Master. Straight ahead."
            </p>
            <p>
              He walks through the automatic doors.
            </p>
          </section>

          <Pulse />

          {/* The Wall */}
          <section className="calc-section reveal">
            <h2>The Wall</h2>
            <p>
              It hits him in the chest.
            </p>
            <p>
              Not a sound. Not a sight. A feeling. A pressure that starts behind his sternum
              and spreads outward through his ribs. His skin prickles. His jaw tightens. His
              breath catches for half a second before he overrides it with a controlled exhale.
            </p>
            <p>
              He knows what this is. He has read about it a thousand times. Mirror neurons. The
              human nervous system's involuntary response to the emotional states of nearby
              organisms. The body reads the room before the mind can. It absorbs the ambient
              chemistry like a sponge dropped into water.
            </p>
            <p>
              But knowing what it is and feeling it are two completely different things.
            </p>
            <p>
              A woman thirty feet to his right sees him. Her hand moves to her daughter's
              shoulder. She does not pull the child closer. She does not step back. Her hand
              just moves. An involuntary repositioning of the body between a perceived threat
              and a dependent. She does not know she did it. Her amygdala did it for her.
            </p>
            <p>
              A man at the coffee kiosk looks up. His eyes widen for exactly one-fifth of a
              second. The micro-expression is textbook. She had shown him the research.
              Ekman's facial coding system. The involuntary widening that signals surprise
              transitioning to fear. One-fifth of a second. Then the man looks down at his
              phone and pretends to read something.
            </p>
            <p>
              A teenager nudges another teenager. The nudge says look without saying look. The
              second teenager glances. Then both of them look away too fast.
            </p>
            <p>
              None of them are cruel. He knows that. None of them made a decision. Their
              nervous systems processed his face in 400 milliseconds and generated a response
              that was ancient before language existed. Tusk. Large. Unknown. The amygdala
              does not nuance. It categorizes. And the categories were written in a time when
              getting the assessment wrong meant dying.
            </p>
            <p>
              He keeps walking. His cortisol is rising. Not because anyone has spoken to him.
              Not because anyone has done anything. Because he can feel their cortisol, and
              his mirror neurons are doing exactly what mirror neurons do. He is absorbing
              the fear of strangers who are afraid of him, and his body is processing their
              fear as though it were his own.
            </p>
            <p>
              This is the part she could not teach. She was never afraid of him. There was
              no fear to mirror in the cabin. There was no wall of ambient cortisol to walk
              through. He had understood the theory perfectly and the experience not at all.
            </p>
          </section>

          <Pulse />

          {/* The Scream */}
          <section className="calc-section reveal">
            <h2>The Scream</h2>
            <p>
              He is halfway through the terminal when it happens.
            </p>
            <p>
              A sound. High. Sharp. Female. The kind of sound that bypasses every cognitive
              filter and plugs directly into the brainstem. A scream.
            </p>
            <p>
              The terminal freezes.
            </p>
            <p>
              Not slowly. Not in sequence. All at once. Four hundred people stop moving in
              the same half-second. Conversations cut mid-syllable. Footsteps halt. Luggage
              wheels go silent. The collective nervous system of the terminal shifts from
              baseline to threat in a single synchronized spike.
            </p>
            <p>
              He feels it happen. The wave. Every nervous system around him dumps adrenaline
              simultaneously and the ambient chemistry of the room changes so fast it is
              almost physical. Like the air pressure dropped. Like the temperature shifted.
              His own adrenaline fires in response. His heart rate jumps. His pupils dilate.
              His hands open at his sides, fingers spreading, the body's involuntary preparation
              to grab or to run.
            </p>
            <p>
              Then he sees it.
            </p>
            <p>
              A woman at Gate 14. Standing. Phone on the ground. Hand over her mouth. She is
              staring at the departure board. She is not in danger. She is not hurt. She is
              reading something on the screen that just changed her life, and the sound that
              came out of her was not fear. It was grief. A canceled flight. A missed connection.
              A funeral she is not going to make it to. He does not know the specifics. He
              knows the chemistry. The sound was a cortisol spike so sudden it bypassed every
              social filter she had and came out raw.
            </p>
            <p>
              The terminal does not know this yet. The terminal heard a scream and four hundred
              amygdalas are still processing. Heads are turning. People are looking for the
              source. Looking for the threat. And because the terminal is already scanning for
              danger, four hundred pairs of eyes are now moving across the room in search
              patterns, and he is standing in the middle of the terminal, seven feet tall with
              tusks and orange eyes, and every single scanning gaze that lands on him produces
              the same micro-expression.
            </p>
            <p>
              Him. They think it is him.
            </p>
            <p>
              He can see it happen in real time. The search pattern resolves. The eyes find the
              largest, most unfamiliar thing in the room. The amygdala, already primed by the
              scream, completes its threat assessment in record time because the cortisol is
              already elevated. The process that normally takes 400 milliseconds takes 200. Fear
              plus novelty equals certainty. The room decides he is the danger before a single
              person thinks about it.
            </p>
            <p>
              His cortisol is screaming. Not because he is afraid. Because four hundred people
              are afraid of him and his mirror neurons cannot tell the difference between their
              fear and his. He is drowning in borrowed chemistry. Every cell in his body is
              telling him to run and he has nowhere to run to and the woman at Gate 14 is still
              standing there with her hand over her mouth and nobody is helping her because
              everyone is looking at him.
            </p>
          </section>

          <Pulse />

          {/* The Breath */}
          <section className="calc-section reveal">
            <h2>The Breath</h2>
            <p>
              Four seconds in.
            </p>
            <p>
              He does it standing in the middle of the terminal with every eye on him. He
              breathes in through his nose for four seconds. His diaphragm drops. His
              shoulders do not move. It is invisible to everyone watching. Inside, his vagus
              nerve fires.
            </p>
            <p>
              Seven seconds hold.
            </p>
            <p>
              The hold is the hardest part. His adrenaline wants action. His cortisol wants
              escape. Holding the breath tells the autonomic nervous system that there is no
              emergency, because an organism in true danger does not pause to hold its breath.
              The hold is a lie his body tells itself, and the body believes it.
            </p>
            <p>
              Eight seconds out.
            </p>
            <p>
              The exhale activates the parasympathetic branch. Heart rate decreases. Cortisol
              production attenuates. The vagal brake engages. He feels it like a hand pressing
              down on a shaking surface. The tremor does not stop. It dampens. The fear is
              still there. But the override is online.
            </p>
            <p>
              He does it again. Four. Seven. Eight.
            </p>
            <p>
              And again.
            </p>
            <p>
              By the third cycle, his hands have stopped shaking. By the fourth, he can think.
              Not react. Think.
            </p>
            <p>
              He thinks: the woman at Gate 14 is still standing alone.
            </p>
          </section>

          <Pulse />

          {/* The Choice */}
          <section className="calc-section reveal">
            <h2>The Choice</h2>
            <p>
              Every person in this terminal is looking at him. If he moves toward the woman,
              four hundred nervous systems will interpret it as the threat advancing. Their
              cortisol will spike higher. Someone might intervene. Someone might call security.
              Someone might put themselves between him and the woman because their amygdala
              has already decided he is the danger and his movement toward a distressed person
              will confirm it.
            </p>
            <p>
              If he does nothing, he is safe. He can stand still, wait for the moment to pass,
              let the terminal recalibrate, let the scanning gazes find something else to land
              on. The butler is somewhere behind him. The gate is ahead. The plane will board.
              None of this is his problem.
            </p>
            <p>
              Except the woman is still standing alone. And nobody is helping her. Because they
              are all looking at him.
            </p>
            <p>
              He understands the math. His presence in this room has redirected the threat
              response of every person in it. The scream activated their chemistry. His face
              gave it a target. And now the woman who actually needs help is invisible because
              the room's entire attentional capacity is consumed by him.
            </p>
            <p>
              He is the reason no one is helping her.
            </p>
            <p>
              He is also the only one who knows it.
            </p>
          </section>

          <Pulse />

          {/* The Frequency */}
          <section className="calc-section reveal">
            <h2>The Frequency</h2>
            <p>
              He starts walking. Toward Gate 14. Toward the woman.
            </p>
            <p>
              He walks slowly. Not because he is unsure. Because speed signals urgency and
              urgency confirms threat. He walks at the pace of someone going somewhere they
              belong. His shoulders are down. His hands are open at his sides, fingers visible.
              Open palms signal non-aggression. This is primate wiring older than language. He
              learned it from a textbook. He is deploying it in an airport.
            </p>
            <p>
              He can feel the room tracking him. Every step produces a micro-adjustment in the
              ambient chemistry. Some people step back without knowing it. Some hold their
              ground. A security guard near the food court reaches for his radio and then stops.
              Watching. Not acting. Not yet.
            </p>
            <p>
              He reaches the woman. She is still staring at the departure board. Her phone is
              still on the floor. She has not moved since the scream. Her body is locked in the
              freeze response. Cortisol and adrenaline both maxed, no action taken, the system
              waiting for a signal that tells it what to do next.
            </p>
            <p>
              He stops three feet away. Not two. Not one. Three. The distance that says "I am
              here" without saying "I am close." The distance that gives the amygdala room to
              assess without triggering approach alarms.
            </p>
            <p>
              He speaks.
            </p>
            <p>
              Low register. Slow pace. Downward inflection at the end. Exactly the way she
              taught him. The frequency that tells a nervous system, before the words arrive,
              that the voice is safe.
            </p>
            <p>
              "I think you dropped your phone."
            </p>
            <p>
              Six words. Not "are you okay." Not "what happened." Not "can I help." Those
              require the woman to assess her own state, which her prefrontal cortex cannot do
              right now because her amygdala has the wheel. "I think you dropped your phone"
              requires nothing. It is a statement about an object. It gives her something
              concrete to process. Something small. Something her brain can grab onto while
              the rest of her chemistry catches up.
            </p>
            <p>
              She looks at him.
            </p>
            <p>
              The flinch is there. In her eyes. In the micro-contraction of the muscles around
              her mouth. She sees the tusks. She sees the size. Her amygdala fires. He watches
              it happen in her face. The 400-millisecond window. The assessment. The flash of
              something primal and involuntary.
            </p>
            <p>
              And then his voice lands. The frequency he chose. The register. The pace. It
              arrives half a second behind the visual and it contradicts everything her eyes
              just told her. The voice says safe. The face says threat. And for one suspended
              moment, her nervous system holds both signals and does not know which to believe.
            </p>
            <p>
              She looks at the floor. She sees the phone.
            </p>
            <p>
              "Oh," she says. "Oh. Thank you."
            </p>
            <p>
              She bends down and picks it up. Her hands are shaking. His were too, six minutes
              ago. He does not tell her that. He does not say anything else. He has done what
              the frequency was for. He gave her nervous system one signal that was not threat.
              One point of data that says: this moment is survivable. The phone is on the ground.
              You can pick it up. The world has not ended. Start there.
            </p>
          </section>

          <Pulse />

          {/* The Man */}
          <section className="calc-section reveal">
            <h2>The Man</h2>
            <p>
              He turns from Gate 14 and a man is standing behind him.
            </p>
            <p>
              Short. Trim suit. Earpiece. Hands folded in front of him in the way that people
              fold their hands when they want you to see that their hands are empty. His
              frequency is identical to the butler's. Flat. Professional. Calm. No cortisol
              in the voice. No micro-expression. Nothing.
            </p>
            <p>
              "This way, sir."
            </p>
            <p>
              Three words. Same construction as the butler. "Your ride is here, Young Master."
              "This way, sir." Short. Declarative. No question mark. No room for negotiation.
              The frequency of people who have been trained to produce no signal at all, to
              be neurochemically invisible, to occupy space without triggering the amygdala
              of the person they are serving.
            </p>
            <p>
              He follows the man. Through a door he did not notice. Down a hallway that does
              not look like the rest of the terminal. No crowds. No luggage. No ambient
              chemistry. The walls are quiet. The lighting is even. The air smells like nothing.
            </p>
            <p>
              "Private boarding, sir. The aircraft is ready."
            </p>
            <p>
              He stops walking.
            </p>
            <p>
              "Who sent you?"
            </p>
            <p>
              The man turns. His face does not change. His frequency does not shift. He has
              been asked this question before by people who pay for the answer.
            </p>
            <p>
              "Your company did, sir. Standard protocol for executive travel."
            </p>
            <p>
              His company. The one that sent him a notification on a Tuesday morning. The one
              whose Slack channel is full of memes about his face. That company arranged for
              a man to meet him at the terminal so he would not have to walk through a crowd
              to board a plane.
            </p>
            <p>
              They knew. Someone in that company knew what an airport would do to him. Someone
              calculated the cost of private boarding against the cost of their CEO walking
              through a terminal full of people who had never seen a Half-Orc before. And they
              chose the hallway.
            </p>
            <p>
              He does not know if it was kindness or liability management. He does not know if
              someone in HR has a protocol or if someone who works there actually thought about
              what his first airport would feel like.
            </p>
            <p>
              He follows the man down the hallway toward a plane that will take him to a
              building full of people who have been making memes about his face for three months.
            </p>
            <p>
              In his pocket, the phone buzzes. He does not look at it. The silence is still
              working.
            </p>
            <p>
              In his other pocket, the letter. Two words. Green ink.
            </p>
            <p>
              He boards the plane.
            </p>
          </section>

          <Pulse />

          {/* The Science */}
          <section className="calc-section reveal">
            <h2>The Science Behind Chapter 2</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Mirror Neuron Cascade</span>
              <code>cortisol_self(t) = &sum;&#7522; mirror_weight&#7522; &middot; cortisol_other&#7522;(t)</code>
              <span className="calc-eq-desc">Mirror neurons cause an observer's stress chemistry to rise in proportion to the stress they detect in surrounding nervous systems. In a crowd, the effect is additive. A single person's cortisol can rise to crisis levels without any direct threat, simply from absorbing the ambient fear of hundreds of people responding to their presence. This is not empathy. It is involuntary neurochemical contagion.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">Threat Confirmation Bias</span>
              <code>if cortisol_ambient &gt; &theta;_primed AND novel_stimulus = present &rarr; threat_assessment_time halves</code>
              <span className="calc-eq-desc">When a group's cortisol is already elevated (from a scream, an alarm, any sudden stimulus), the amygdala's threat assessment of new stimuli accelerates. A face that would take 400 milliseconds to assess at baseline takes 200 milliseconds when the system is primed. Faster assessment means less prefrontal intervention. Less intervention means more reliance on appearance-based categorization. Fear makes prejudice faster.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Concrete Anchor</span>
              <code>if target_state = freeze AND input = abstract &rarr; no processing<br />if target_state = freeze AND input = concrete &rarr; prefrontal re-engagement</code>
              <span className="calc-eq-desc">"Are you okay?" is an abstract question that requires self-assessment. A person in freeze cannot self-assess because the prefrontal cortex is offline. "You dropped your phone" is a concrete observation about an external object. It gives the brain something specific to process, re-engaging the prefrontal cortex through the object rather than through the self. First responders are trained in this technique. Gnul learned it from a textbook.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">The Three-Foot Rule</span>
              <code>distance &lt; 18in = intimate &rarr; amygdala alarm<br />distance = 3ft = social &rarr; amygdala monitoring<br />distance &gt; 6ft = public &rarr; amygdala baseline</code>
              <span className="calc-eq-desc">Edward Hall's proxemic zones are not cultural preferences. They are neurochemical boundaries. At intimate distance, the amygdala is in alarm mode. At social distance, it monitors without escalating. Gnul chose three feet because it is close enough to communicate care and far enough to avoid triggering approach alarms in a nervous system that is already in crisis.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Neurochemical Invisibility</span>
              <code>voice_flat + expression_neutral + posture_open = minimum amygdala activation in observer</code>
              <span className="calc-eq-desc">The butler and the boarding agent share a trained frequency: neurochemical invisibility. A flat vocal register, neutral expression, and open posture produce the minimum possible amygdala activation in the people they serve. This is not coldness. It is professional compassion. They remove themselves as a variable so the person they are serving can process their own chemistry without adding another signal to the noise.</span>
            </div>
          </section>

          <Pulse />

          {/* Navigation */}
          <section className="calc-section reveal" style={{ textAlign: 'center', padding: '2rem 0' }}>
            <a href="/gnul/chapter-1" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>&larr; Chapter 1: The Notification</a>
            <span style={{ color: '#333', margin: '0 2rem' }}>|</span>
            <a href="/gnul/chapter-3" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>Chapter 3: The Landing &rarr;</a>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>Narrative Physics&trade; Leadership Development</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default GnulChapter2;
